import React, {ReactNode} from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {EndlessList, mapListConfig} from 'components/ui-kit/EndlessList2.0';
import {
    filtersChanged,
    filtersReset,
    filtersSubmit,
    listState$,
    pageIncremented,
    sortingsChanged,
} from 'models/virtualWarehouse/grid/list';
import {
    GridFieldConfig,
    VirtualWarehouseGridAccessors,
    VirtualWarehouseGridFormattedRow,
    VirtualWarehouseGridRawRow,
} from 'types';

import {ListRow, Text, FlexRowElement} from './styled';

interface Props {
    configFunc: (t: TFunction, brands: any[], groups: any[]) => GridFieldConfig[];
    dataMapper: (
        data: VirtualWarehouseGridRawRow[] | undefined,
        t: TFunction,
    ) => VirtualWarehouseGridFormattedRow[];
    headerControls?: ReactNode;
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
    showConfigBlock?: boolean;
}

export const VirtualWarehouseGridList = ({
    configFunc,
    dataMapper,
    headerControls,
    exportConfig,
}: Props) => {
    const {t} = useTranslation();
    const {dataUngrouped: data, sortings, filters, hasNext, showConfigBlock} = useStore(listState$);

    const mappedData = React.useMemo(() => dataMapper(data ?? [], t), [dataMapper, data, t]);

    return (
        <>
            <EndlessList<VirtualWarehouseGridFormattedRow>
                rows={mappedData}
                // totalRowsCount={totalCount}
                hasMore={hasNext}
                headerControls={headerControls}
                renderRow={(row: VirtualWarehouseGridFormattedRow) => (
                    <ListRow>
                        <FlexRowElement>
                            <Text isGray>{t('pages.virtualWarehouse.groupCode')}</Text>
                            <Text textAlign="right">
                                {row[VirtualWarehouseGridAccessors.groupCode]}
                            </Text>
                        </FlexRowElement>
                        <FlexRowElement>
                            <Text isGray>{t('pages.virtualWarehouse.name')}</Text>
                            <Text textAlign="right">{row[VirtualWarehouseGridAccessors.name]}</Text>
                        </FlexRowElement>
                        <FlexRowElement>
                            <Text isGray>{t('pages.virtualWarehouse.gtin')}</Text>
                            <Text textAlign="right">{row[VirtualWarehouseGridAccessors.gtin]}</Text>
                        </FlexRowElement>
                        <FlexRowElement>
                            <Text isGray>{t('pages.virtualWarehouse.trademark')}</Text>
                            <Text textAlign="right">
                                {row[VirtualWarehouseGridAccessors.trademark]}
                            </Text>
                        </FlexRowElement>
                        <FlexRowElement>
                            <Text isGray>{t('pages.virtualWarehouse.markingCodes')}</Text>
                            <Text textAlign="right">{row[VirtualWarehouseGridAccessors.code]}</Text>
                        </FlexRowElement>
                        <FlexRowElement>
                            <Text isGray>{t('pages.virtualWarehouse.packaging')}</Text>
                            <Text textAlign="right">{row[VirtualWarehouseGridAccessors.type]}</Text>
                        </FlexRowElement>
                        <FlexRowElement>
                            <Text isGray>{t('pages.virtualWarehouse.storageName')}</Text>
                            <Text textAlign="right">
                                {row[VirtualWarehouseGridAccessors.storageName]}
                            </Text>
                        </FlexRowElement>
                        <FlexRowElement>
                            <Text isGray>{t('pages.virtualWarehouse.storageAddress')}</Text>
                            <Text textAlign="right">
                                {row[VirtualWarehouseGridAccessors.storageAddress]}
                            </Text>
                        </FlexRowElement>
                        <FlexRowElement>
                            <Text isGray>{t('pages.virtualWarehouse.owner')}</Text>
                            <Text textAlign="right">
                                {row[VirtualWarehouseGridAccessors.participant]}
                            </Text>
                        </FlexRowElement>
                    </ListRow>
                )}
                onNextPage={() => pageIncremented()}
                configurationBlock={mapListConfig(
                    configFunc(t, [], []),
                    filters,
                    sortings,
                    (value) => filtersChanged([value]),
                    filtersSubmit,
                    filtersReset,
                    sortingsChanged,
                    showConfigBlock,
                )}
                exportConfig={exportConfig}
            />
        </>
    );
};
